import {
	type Icon,
	IconBlockquote,
	IconBold,
	IconBookmark,
	IconBookmarks,
	IconCalculator,
	IconCheckbox,
	IconCode,
	IconColumns,
	IconHighlight,
	IconInfoCircle,
	IconItalic,
	IconLetterT,
	IconLink,
	IconList,
	IconListNumbers,
	IconMath,
	IconMessageCircle,
	IconMinus,
	IconNote,
	IconPencil,
	IconPhoto,
	IconPilcrow,
	IconPlayerPlay,
	IconQuote,
	IconSparkles,
	IconSuperscript,
	IconTable,
	IconTableImport,
	IconTypography,
	IconVectorTriangle,
} from "@tabler/icons-react";
import type { ToolboxMode } from "./types";

// Section taxonomy must match the SectionSlug union in Toolbox.tsx.
export type BlockSection =
	| "text"
	| "lists"
	| "math"
	| "media"
	| "refs"
	| "layout";
// Flow registry sections — user direction 2026-05-13 reverted the
// INLINE-only constraint: "we had a full menu for flow mode, i need
// it full, no need for filtering anymore as we now do not apply it
// to block mode" (Block view has no Toolbox sidebar; Flow inherits
// the full structural set + inline).
export type FlowSection =
	| "inline"
	| "text"
	| "lists"
	| "math"
	| "media"
	| "refs"
	| "layout";

export interface ToolboxItem {
	/** Unique slug — used in `data-testid="toolbox-item-${slug}"` and analytics. */
	slug: string;
	/** Visible label (sentence case). */
	label: string;
	/** Tabler icon for the item button. */
	icon: Icon;
	/** Insert payload — passed to `onInsert` when activated. */
	kind: string;
	variant?: string;
	/** False when the underlying block kind / inline command isn't wired yet. */
	supported: boolean;
	/** Optional hint shown as title attr; falls back to label. */
	hint?: string;
}

// ------------------------------------------------------------------
// Block-mode registry (Engineering brief §C1, "Block-mode contents")
// ------------------------------------------------------------------

export const BLOCK_REGISTRY: Record<BlockSection, ToolboxItem[]> = {
	text: [
		{
			slug: "paragraph",
			label: "Paragraph",
			icon: IconPilcrow,
			kind: "paragraph",
			supported: true,
		},
		{
			slug: "heading-1",
			label: "Heading 1",
			icon: IconTypography,
			kind: "heading",
			variant: "1",
			supported: true,
		},
		{
			slug: "heading-2",
			label: "Heading 2",
			icon: IconLetterT,
			kind: "heading",
			variant: "2",
			supported: true,
		},
		{
			slug: "heading-3",
			label: "Heading 3",
			icon: IconLetterT,
			kind: "heading",
			variant: "3",
			supported: true,
		},
		{
			slug: "blockquote",
			label: "Quote",
			icon: IconBlockquote,
			kind: "blockquote",
			supported: true,
		},
		{
			slug: "callout",
			label: "Callout",
			icon: IconInfoCircle,
			kind: "callout",
			supported: true,
		},
	],
	lists: [
		{
			slug: "list-bullet",
			label: "Bullet list",
			icon: IconList,
			kind: "list",
			variant: "bullet",
			supported: true,
		},
		{
			slug: "list-numbered",
			label: "Numbered list",
			icon: IconListNumbers,
			kind: "list",
			variant: "numbered",
			supported: true,
		},
		{
			slug: "list-task",
			label: "Task list",
			icon: IconCheckbox,
			kind: "list",
			variant: "task",
			supported: false,
			hint: "Coming soon",
		},
	],
	math: [
		{
			slug: "equation",
			label: "Equation",
			icon: IconCalculator,
			kind: "equation",
			supported: true,
		},
		{
			slug: "theorem",
			label: "Theorem",
			icon: IconNote,
			kind: "theorem",
			variant: "theorem",
			supported: true,
		},
		{
			slug: "lemma",
			label: "Lemma",
			icon: IconNote,
			kind: "theorem",
			variant: "lemma",
			supported: true,
		},
		{
			slug: "proof",
			label: "Proof",
			icon: IconNote,
			kind: "theorem",
			variant: "proof",
			supported: true,
		},
		{
			slug: "definition",
			label: "Definition",
			icon: IconNote,
			kind: "theorem",
			variant: "definition",
			supported: true,
		},
	],
	media: [
		{
			slug: "image",
			label: "Image",
			icon: IconPhoto,
			kind: "figure",
			variant: "image",
			supported: true,
		},
		{
			slug: "figure",
			label: "Figure",
			icon: IconPhoto,
			kind: "figure",
			supported: true,
		},
		// TikZ step 3 (1b): a figure drawn from TikZ source, opened on the
		// split view's template gallery.
		{
			slug: "figure-tikz",
			label: "TikZ figure",
			icon: IconVectorTriangle,
			kind: "figure",
			variant: "tikz",
			supported: true,
			hint: "Drawn from TikZ source · starts from a template",
		},
		// TikZ step 3 (1c): Ask Lilia draws it; it is inserted once it drew.
		{
			slug: "figure-tikz-ask",
			label: "Draw with Ask Lilia…",
			icon: IconSparkles,
			kind: "figure",
			variant: "tikz-ask",
			supported: true,
			hint: "Describe a figure · Ask Lilia draws it in TikZ",
		},
		// Tables were dropped from both registries in the May restructure (#84)
		// and never came back; the insert handler kept supporting them and the
		// empty state kept suggesting them. Found by the editor-core e2e spec.
		{
			slug: "table",
			label: "Table",
			icon: IconTable,
			kind: "table",
			supported: true,
			hint: "Rows and columns · booktabs in LaTeX",
		},
		// A table from the author's Tables, linked or copied into this paper.
		{
			slug: "existing-table",
			label: "Existing table…",
			icon: IconTableImport,
			kind: "existingTable",
			supported: true,
			hint: "One of your tables · link it or take a copy",
		},
		{
			slug: "video",
			label: "Video",
			icon: IconPlayerPlay,
			kind: "embed",
			variant: "video",
			supported: false,
			hint: "Coming soon",
		},
		{
			slug: "code",
			label: "Code block",
			icon: IconCode,
			kind: "code",
			supported: true,
		},
	],
	refs: [
		{
			slug: "citation",
			label: "Citation",
			icon: IconQuote,
			kind: "citation",
			supported: false,
			hint: "Inline command — wires in T04",
		},
		{
			slug: "cross-ref",
			label: "Cross-reference",
			icon: IconBookmarks,
			kind: "cross-ref",
			supported: false,
			hint: "Inline command — wires in T04",
		},
		{
			slug: "footnote",
			label: "Footnote",
			icon: IconSuperscript,
			kind: "footnote",
			supported: true,
		},
		{
			slug: "bibliography",
			label: "Bibliography",
			icon: IconBookmark,
			kind: "bibliography",
			supported: true,
		},
	],
	layout: [
		{
			slug: "columns",
			label: "Columns",
			icon: IconColumns,
			kind: "columnBreak",
			supported: true,
		},
		// NOTE: a "divider" entry used to sit here, labelled "Divider" and
		// described in the insert menu as "Horizontal rule". It mapped to
		// kind: "pageBreak", and insertHandler ignores `variant` for that kind —
		// so it produced a byte-identical block to the entry below. Picking
		// "Horizontal rule" expecting a thin line gave you a page break.
		//
		// Removed rather than relabelled: two menu items emitting the same block
		// is the actual defect. The `divider` block TYPE is retained on the
		// server for imported content (EPUB/DOCX still produce it).
		//
		// A real horizontal rule is a missing feature, not a mislabelled one.
		{
			slug: "page-break",
			label: "Page break",
			icon: IconMinus,
			kind: "pageBreak",
			supported: true,
		},
	],
};

// ------------------------------------------------------------------
// Flow-mode registry — restored to the FULL set 2026-05-13 per user
// direction: "we had a full menu for flow mode, i need it full, no
// need for filtering anymore as we now do not apply it to block
// mode". Block view doesn't mount a Toolbox sidebar today; Flow is
// the only surface that gets the full structural + inline menu.
//
// INLINE comes first because it's the most-clicked group on Flow
// (Citation / Inline math / Cross-ref / Footnote / Margin note).
// Structural blocks follow — same TEXT / LISTS / MATH / MEDIA /
// REFERENCES / LAYOUT groups as BLOCK_REGISTRY.
// ------------------------------------------------------------------

export const FLOW_REGISTRY: Record<FlowSection, ToolboxItem[]> = {
	inline: [
		{
			slug: "citation",
			label: "Citation",
			icon: IconQuote,
			kind: "citation",
			supported: true,
			hint: "Numeric or author-year",
		},
		{
			slug: "inline-math",
			label: "Inline math",
			icon: IconMath,
			kind: "mark",
			variant: "math",
			supported: true,
			hint: "Math within prose · $x^2$",
		},
		{
			slug: "cross-reference",
			label: "Cross-reference",
			icon: IconBookmarks,
			kind: "cross-ref",
			supported: true,
			hint: "Point at a table, figure or equation · opens the @ picker",
		},
		{
			slug: "footnote",
			label: "Footnote",
			icon: IconSuperscript,
			kind: "footnote",
			supported: true,
			hint: "Numbered note at page foot",
		},
		{
			slug: "margin-note",
			label: "Margin note",
			icon: IconNote,
			kind: "margin-note",
			supported: false,
			hint: "Side annotation · wires in follow-up",
		},
	],
	text: BLOCK_REGISTRY.text,
	lists: BLOCK_REGISTRY.lists,
	math: BLOCK_REGISTRY.math,
	media: BLOCK_REGISTRY.media,
	// Inline-citation / inline-footnote / inline-cross-ref already live
	// in the INLINE section above, so drop them from REFS on Flow.
	// Keep `bibliography` — that's the bibliography block (full block,
	// not an inline node), which has no equivalent in INLINE.
	refs: BLOCK_REGISTRY.refs.filter((item) => item.kind === "bibliography"),
	layout: BLOCK_REGISTRY.layout,
};

/** Convenience accessor — picks the right registry per mode. */
export function getRegistry(mode: ToolboxMode): Record<string, ToolboxItem[]> {
	return mode === "block" ? BLOCK_REGISTRY : FLOW_REGISTRY;
}

/** Flat list of all items for the current mode — useful for search/filter. */
export function getAllItems(mode: ToolboxMode): ToolboxItem[] {
	const reg = getRegistry(mode);
	return Object.values(reg).flat();
}
